<template>
    <div>
        <fullscreen-loading v-if="loading" />

        <div class="card">
            <div class="card-header pb-3">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <span class="h4" v-if="!embed">{{ $t('songs.library_title') }}</span>
                    </div>
                    <div class="text-right">
                        <div class="actions mr-n2">
                            <div class="float-right d-flex align-items-center">
                                <div class="mr-3" v-if="hasPermission('showArchivedSongs')">
                                    <select
                                        @change="load(1)"
                                        class="form-control form-control-filtering"
                                        v-model="filtering.archived"
                                    >
                                        <option :value="false">{{ $t('songs.show_active') }}</option>
                                        <option :value="true">{{ $t('songs.show_archived') }}</option>
                                    </select>
                                </div>

                                <div>
                                    <button
                                        class="btn btn-secondary btn-sm rounded-circle btn-icon-only float-right"
                                        @click="showUpload = true"
                                    >
                                        <icon name="plus"></icon>
                                    </button>
                                    <h2 class="h6 mt-2 mr-2 float-right">{{ $t('songs.upload_title') }}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <table class="table table-sm table-no-side-paddings table-striped">
                    <thead>
                    <tr>
                        <ordering
                            @change="load()"
                            v-model="order"
                            field="display_name"
                            tag="th"
                            :placeholder="$t('songs.title_placeholder')"
                        >
                            {{ $t('songs.title') }}
                        </ordering>
                        <ordering
                            @change="load()"
                            v-model="order"
                            field="author"
                            tag="th"
                            :placeholder="$t('songs.artist_placeholder')"
                        >
                            {{ $t('songs.artist') }}
                        </ordering>
                        <ordering
                            @change="load()"
                            v-model="order"
                            field="album"
                            tag="th"
                            :placeholder="$t('songs.album_placeholder')"
                        >
                            {{ $t('songs.album') }}
                        </ordering>
                        <ordering
                            @change="load()"
                            v-model="order"
                            field="duration"
                            tag="th"
                            :placeholder="$t('songs.duration_placeholder')"
                        >
                            {{ $t('songs.duration') }}
                        </ordering>
                        <th />
                        <th />
                        <th class="text-right">{{ $t('songs.funeral_count') }}</th>
                        <ordering
                            @change="load()"
                            v-model="order"
                            field="in_library"
                            class="text-right"
                            tag="th"
                        >
                            {{ $t('songs.visible_in_library') }}
                        </ordering>
                        <th class="text-right">{{ $t('songs.duplicates') }}</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr>
                        <td>
                            <input
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                type="text"
                                v-model="filtering.display_name"
                                :placeholder="$t('songs.title_placeholder')"
                            />
                        </td>
                        <td>
                            <input
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                type="text"
                                v-model="filtering.author"
                                :placeholder="$t('songs.artist_placeholder')"
                            />
                        </td>
                        <td>
                            <input
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                type="text"
                                v-model="filtering.album"
                                :placeholder="$t('songs.album_placeholder')"
                            />
                        </td>
                        <td>
                            <input
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                type="text"
                                v-model="filtering.duration"
                                :placeholder="$t('songs.duration_placeholder')"
                            />
                        </td>
                        <td />
                        <td />
                        <td />
                        <td>
                            <select
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                v-model="filtering.in_library"
                            >
                                <option :value="null">{{ $t('songs.choose_option') }}</option>
                                <option :value="true">{{ $t('songs.yes') }}</option>
                                <option :value="false">{{ $t('songs.no') }}</option>
                            </select>
                        </td>
                        <td>
                            <select
                                @change="load(1)"
                                class="form-control form-control-filtering"
                                v-model="duplicate_filter"
                            >
                                <option :value="null">{{ $t('songs.show_all') }}</option>
                                <option :value="2">{{ $t('songs.show_non_duplicates') }}</option>
                                <option :value="1">{{ $t('songs.show_duplicates') }}</option>
                            </select>
                        </td>
                    </tr>

                    <tr v-for="song of songs.data" :key="song.id">
                        <td>
                            <div class="d-flex align-items-center">
                                <img
                                    style="width: 25px; height: 25px;"
                                    :src="song.cover ? song.cover_public_path : '/images/audio.png'"
                                    class="avatar rounded-circle float-left mr-2"
                                />
                                <icon
                                    v-if="song.duplicate"
                                    name="exclamation-triangle"
                                    :v-tooltip="$t('songs.duplicate_tooltip')"
                                ></icon>
                                <span v-if="song.duplicate">&nbsp;</span>
                                <span style="white-space: pre-wrap">{{ song.display_name }}</span>
                            </div>
                        </td>
                        <td>
                            <span style="white-space: pre-wrap">{{ song.author }}</span>
                        </td>
                        <td>
                            <span style="white-space: pre-wrap">{{ song.album }}</span>
                        </td>
                        <td>{{ $root.minutesToTimeInput(song.duration) }}</td>
                        <td>
                            <song-player
                                :preload="'none'"
                                :src="song.public_path"
                                :key="'song_' + song.id"
                            />
                        </td>
                        <td>
                            <a
                                :href="song.public_path"
                                :download="song.name"
                                style="margin-top: 5px; display: inline-block;"
                            >
                                <icon name="cloud-download-alt" />
                            </a>
                        </td>
                        <td class="text-right">
                                <span
                                    v-tooltip="song.ceremony_count ? $t('songs.funeral_count_tooltip', { count: song.ceremony_count }) : null"
                                >
                                    {{ song.ceremony_count }}
                                </span>
                        </td>
                        <td class="text-right">{{ song.in_library ? $t('songs.yes') : $t('songs.no') }}</td>
                        <td class="text-right">
                            <button
                                @click="archiveSong(song)"
                                class="btn btn-xs rounded-pill btn-warning"
                                :v-tooltip="$t('songs.archive_tooltip')"
                            >
                                <icon name="trash" scale="0.8"></icon>
                            </button>
                            <button
                                @click="editSong(song)"
                                class="btn btn-xs rounded-pill btn-primary"
                                :v-tooltip="$t('songs.edit_tooltip')"
                            >
                                <icon name="pencil-alt" scale="0.8"></icon>
                            </button>
                        </td>
                    </tr>

                    <tr v-if="songs.data.length === 0 && !loading">
                        <td colspan="9" class="no-results">{{ $t('songs.no_results') }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div class="card-footer border-0">
                <Pagination :pagination="songs" @change="load" />
            </div>
        </div>

        <modal :show="showUpload" :closable="!loading" @close="showUpload = false">
            <div slot="header">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('songs.upload_title') }}</h5>
                    <button type="button" class="close" @click="showUpload = false" aria-label="Close"><span aria-hidden="true">×</span></button>
                </div>
            </div>

            <div slot="body">
                <dropzone-upload
                    :url="`/api/songs/upload`"
                    :extensions_text="'.mp3,.wav,.wma,.flac,.aac,.ac3,.m4a,.opus'"
                    :icon-only="false"
                    :unique_number_per_dropzone="1"
                    :max_files="100"
                    :max_size_mb_per_file="150"
                    :bestand="[]"
                    :unique_string_per_dropzone="'song'"
                    @queueComplete="songsUploaded">
                </dropzone-upload>
            </div>

            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="showUpload = false">{{ $t('general.close') }}</button>
            </div>
        </modal>

        <modal :show="song_to_archive" :closable="!loading" @close="song_to_archive = null">
            <div slot="header">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('songs.archive_title') }}</h5>
                    <button type="button" class="close" @click="song_to_archive = null" aria-label="Close"><span aria-hidden="true">×</span></button>
                </div>
            </div>

            <div slot="body" v-if="song_to_archive">
                {{ $t('songs.archive_confirmation') }}
                <strong>{{ song_to_archive.display_name }} - {{ song_to_archive.author }}</strong>
            </div>

            <div slot="footer">
                <button type="button" class="btn btn-primary btn-sm" @click="putSong()">{{ $t('songs.archive_tooltip') }}</button>
                <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="song_to_archive = null">{{ $t('general.close') }}</button>
            </div>
        </modal>


        <modal :show="songData" :closable="!loading" @close="songData = null">
            <div slot="header">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('songs.edit_title') }}</h5>
                    <button type="button" class="close" @click="songData = null" aria-label="Close"><span aria-hidden="true">×</span></button>
                </div>
            </div>

            <div slot="body" v-if="songData">
                <div class="form-group row">
                    <label class="form-control-label col-3">{{ $t('general.name') }}</label>
                    <div class="col-9">
                        <input type="text" class="form-control form-control-sm" v-model="songData.display_name">
                        <form-errors :errors="songErrors" field="display_name" />
                    </div>
                </div>

                <div class="form-group row">
                    <label class="form-control-label col-3">{{ $t('songs.artist') }}</label>
                    <div class="col-9">
                        <input type="text" class="form-control form-control-sm" v-model="songData.author">
                        <form-errors :errors="songErrors" field="author" />
                    </div>
                </div>

                <div class="form-group row">
                    <label class="form-control-label col-3">{{ $t('songs.album') }}</label>
                    <div class="col-9">
                        <input type="text" class="form-control form-control-sm" v-model="songData.album">
                        <form-errors :errors="songErrors" field="album" />
                    </div>
                </div>

                <div class="form-group row">
                    <label class="form-control-label col-3">{{ $t('songs.visible_in_library') }}</label>
                    <div class="col-9">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="speaker" name="speaker" class="custom-control-input" :value="true" v-model="songData.in_library">
                            <label class="custom-control-label" for="speaker">{{ $t('songs.yes') }}</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="media" name="speaker" class="custom-control-input" :value="false" v-model="songData.in_library">
                            <label class="custom-control-label" for="media">{{ $t('songs.no') }}</label>
                        </div>

                        <form-errors :errors="songErrors" field="in_library" />
                    </div>
                </div>

                <div class="form-group row" v-if="hasPermission('manageWelcomeSigns') && companySettings?.welcome_sign_music_enabled">
                    <label class="form-control-label col-3">{{ $t('songs.default_for_welcome_signs') }}</label>
                    <div class="col-9">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="default_for_welcome_signs_yes" name="default_for_welcome_signs_yes" class="custom-control-input" :value="true" v-model="songData.default_for_welcome_signs">
                            <label class="custom-control-label" for="default_for_welcome_signs_yes">{{ $t('songs.yes') }}</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="default_for_welcome_signs_no" name="default_for_welcome_signs_no" class="custom-control-input" :value="false" v-model="songData.default_for_welcome_signs">
                            <label class="custom-control-label" for="default_for_welcome_signs_no">{{ $t('songs.no') }}</label>
                        </div>

                        <form-errors :errors="songErrors" field="default_for_welcome_signs" />
                    </div>
                </div>
            </div>

            <div slot="footer">
                <button type="button" class="btn btn-primary btn-sm" @click="saveSong">{{ $t('files.save') }}</button>
                <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="songData = null">{{ $t('general.close') }}</button>
            </div>
        </modal>
    </div>
</template>

<script>

    import Pagination from '../../components/Pagination';
    import Modal from '../../components/Modal';
    import DropzoneUpload from '../../components/DropzoneUpload';
    import FullscreenLoading from '../../components/FullscreenLoading';
    import SongPlayer from '../../components/SongPlayer';
    import { mapGetters, mapState } from 'vuex';
    import Ordering from '../../components/Ordering';
    import FormErrors from '../../components/FormErrors';
    import SongMixin from '../mixins/SongMixin';

    export default {

        name: 'Songs',

        mixins: [
            SongMixin,
        ],

        components: {
            Pagination,
            Modal,
            DropzoneUpload,
            FullscreenLoading,
            SongPlayer,
            Ordering,
            FormErrors,
        },

        data() {
            return {
                duplicate_filter: null,
                loading: false,
                songs: {
                    current_page: 1,
                    data: [],
                },
                showUpload: false,

                songData: null,
                song_to_archive: null,
                songErrors: null,

                order: 'created_at,desc',
                filtering: {
                    archived: false,
                    display_name: null,
                    author: null,
                    album: null,
                    duration: null,
                    in_library: null,
                    duplicates: null,
                },

                songUrl: '/api/songs',
            };
        },

        computed: {
            embed() {
                return window.location.search.includes('embed=true');
            },

            ...mapState({
                token: state => state.token,
                user: state => state.user,
                companySettings: state => state.companySettings,
            }),

            ...mapGetters([
                'hasPermission'
            ])
        },

        created() {
            this.load(1);
        },

        methods: {

            songsUploaded() {
                this.showUpload = false;
                this.load(1);
            },

            editSong(song) {
                this.songErrors = null;
                this.songData = {
                    ...song,
                };
            },

            archiveSong(song) {
                this.songErrors = null;
                this.song_to_archive = {
                    ...song,
                };
            },

            async putSong() {

                this.loading = true;

                try {
                    const response = await this.$http.put(`/api/songs/${this.song_to_archive.id}/archive`);

                    this.song_to_archive = null;

                } catch (exception) {
                    if (!exception.response || (exception.response && exception.response.status !== 401)) {
                        alert('Failed to archive the song');
                    }
                }

                this.load(this.songs.current_page);
                this.loading = false;
            },

            async saveSong() {
                this.loading = true;

                try {
                    const response = await this.$http.put('/api/songs/' + this.songData.id, this.songData);

                    this.songs.data = this.songs.data.map(song => {
                        if (song.id === response.data.id) {
                            return response.data;
                        }

                        return song;
                    });

                    this.songData = null;
                } catch (exception) {
                    if (exception.response && exception.response.status === 422) {
                        this.songErrors = exception.response.data.errors;
                    } else {
                        if (!exception.response || (exception.response && exception.response.status !== 401)) {
                            alert('Failed to save the song');
                        }
                    }
                }

                this.loading = false;
            },
        },
    };

</script>
